/* eslint-disable @typescript-eslint/no-explicit-any */
// ReactivateAccount.tsx
import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useDarkMode } from "../context/DarkModeContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, CheckCircle, AlertCircle } from "lucide-react";

export default function ReactivateAccount() {
  const { completeAccountReactivation } = useAuth();
  const { isDark } = useDarkMode();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!token) {
      setError("Invalid reactivation link. Please request a new one.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }

    setLoading(true);

    try {
      // Make sure to pass the token from URL params
      await completeAccountReactivation(token, newPassword);
      setSuccess(true);

      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (err: any) {
      // Enhanced error handling
      if (err.code === "INVALID_TOKEN") {
        setError(
          "Invalid or expired reactivation token. Please request a new one.",
        );
      } else if (err.code === "REACTIVATION_FAILED") {
        setError("Failed to reactivate account. Please contact support.");
      } else {
        setError(
          err.message || "Failed to reactivate account. Please try again.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center px-6 py-12 ${
          isDark ? "bg-gray-900" : "bg-sky-50"
        }`}
      >
        <div
          className={`w-full max-w-md p-8 rounded-2xl shadow-lg ${
            isDark ? "bg-gray-800" : "bg-white"
          }`}
        >
          <div
            className={`p-4 rounded-md border ${
              isDark
                ? "bg-red-900/30 border-red-800 text-red-300"
                : "bg-red-100 border-red-200 text-red-700"
            }`}
          >
            <div className="flex items-center space-x-2">
              <AlertCircle size={20} />
              <span>Invalid reactivation link. Please request a new one.</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center px-6 py-12 ${
          isDark ? "bg-gray-900" : "bg-sky-50"
        }`}
      >
        <div
          className={`w-full max-w-md p-8 rounded-2xl shadow-lg ${
            isDark ? "bg-gray-800" : "bg-white"
          }`}
        >
          <div
            className={`p-4 rounded-md border ${
              isDark
                ? "bg-green-900/30 border-green-800 text-green-300"
                : "bg-green-100 border-green-200 text-green-700"
            }`}
          >
            <div className="flex items-center space-x-2">
              <CheckCircle size={20} />
              <span>
                Account reactivated successfully! Redirecting to login...
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen flex items-center justify-center px-6 py-12 ${
        isDark ? "bg-gray-900" : "bg-sky-50"
      }`}
    >
      <div
        className={`w-full max-w-md p-8 rounded-2xl shadow-lg ${
          isDark ? "bg-gray-800" : "bg-white"
        }`}
      >
        <h1 className="text-2xl font-bold text-center mb-6">
          Reactivate Your Account
        </h1>

        {error && (
          <div
            className={`p-3 rounded-md border mb-4 ${
              isDark
                ? "bg-red-900/30 border-red-800 text-red-300"
                : "bg-red-100 border-red-200 text-red-700"
            }`}
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              className={`block mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}
            >
              New Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                className={`w-full px-4 py-2 pr-10 border rounded-md ${
                  isDark
                    ? "bg-gray-700 border-gray-600 text-white"
                    : "bg-white border-gray-300 text-gray-900"
                }`}
                placeholder="Enter new password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className={`absolute right-3 top-2 ${
                  isDark ? "text-gray-400" : "text-gray-500"
                }`}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div>
            <label
              className={`block mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}
            >
              Confirm New Password
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className={`w-full px-4 py-2 pr-10 border rounded-md ${
                  isDark
                    ? "bg-gray-700 border-gray-600 text-white"
                    : "bg-white border-gray-300 text-gray-900"
                }`}
                placeholder="Confirm new password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className={`absolute right-3 top-2 ${
                  isDark ? "text-gray-400" : "text-gray-500"
                }`}
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-gradient-to-r from-sky-500 to-blue-600 text-white rounded-md hover:from-sky-600 hover:to-blue-700 disabled:opacity-50 transition"
          >
            {loading ? "Reactivating..." : "Reactivate Account"}
          </button>
        </form>
      </div>
    </div>
  );
}
